import { Component, OnInit } from '@angular/core';
import { RefundVouchersService } from '../../services/refund-vouchers.service';
import { LoadingService } from '../../services/loading.service';
import { ProjectService } from '../../services/project.service';

@Component({
  selector: 'app-refund-vouchers',
  templateUrl: './refund-vouchers.component.html',
  styleUrls: ['./refund-vouchers.component.scss']
})
export class RefundVouchersComponent implements OnInit {
  refundVouchers: any[] = [];
  filteredVouchers: any[] = [];
  
  // Filter properties
  searchTerm: string = '';
  selectedStatus: string = '';
  selectedRefundType: string = '';
  selectedBank: string = '';
  selectedProject: string = '';
  fromDate: Date | null = null;
  toDate: Date | null = null;
  // 🆕 Tipo de fecha para filtrar: 'Creation' (creación) o 'Payment' (pago). Default: DocumentDate (legacy).
  dateType: 'Creation' | 'Payment' | '' = '';
  dateTypes: { value: 'Creation' | 'Payment' | ''; label: string }[] = [
    { value: '', label: 'Fecha documento' },
    { value: 'Creation', label: 'Fecha creación' },
    { value: 'Payment', label: 'Fecha de pago' }
  ];
  
  // Summary metrics
  totalRefundAmount: number = 0;
  totalProcessedAmount: number = 0;
  totalPendingAmount: number = 0;
  avgProcessingTime: number = 0;
  
  // Reference data
  refundStatuses: any[] = [
    { value: 'Pending', label: 'Pendiente' },
    { value: 'Approved', label: 'Aprobado' },
    { value: 'Processing', label: 'En Proceso' },
    { value: 'Completed', label: 'Completado' },
    { value: 'Rejected', label: 'Rechazado' },
    { value: 'Cancelled', label: 'Cancelado' }
  ];
  
  refundTypes: any[] = [
    { value: 'ExcessRefund', label: 'Devolución de Excedente' },
    { value: 'PartialRefund', label: 'Devolución Parcial' },
    { value: 'FullRefund', label: 'Devolución Total' },
    { value: 'ServiceRefund', label: 'Devolución de Servicio' }
  ];
  
  banks: any[] = [];
  projects: any[] = [];

  constructor(
    private refundVouchersService: RefundVouchersService,
    private loadingService: LoadingService,
    private projectService: ProjectService
  ) { }

  ngOnInit(): void {
    this.loadData();
  }

  /**
   * 🆕 Disparado cuando cambia el selector de tipo de fecha o las fechas:
   * re-consulta al backend aplicando el filtro server-side (Creation/Payment/Document).
   */
  onDateFilterChange(): void {
    this.loadData();
  }

  private loadData(): void {
    this.loadingService.show();

    // Cargar devoluciones y proyectos en paralelo
    // 🆕 Si el usuario seleccionó dateType, delegamos filtrado de fecha al backend
    const dateTypeParam = this.dateType || undefined;
    const fromDateParam = this.fromDate ? new Date(this.fromDate) : undefined;
    const toDateParam = this.toDate ? new Date(this.toDate) : undefined;
    const refundVouchers$ = this.refundVouchersService.getAll(
      undefined,
      undefined,
      fromDateParam,
      toDateParam,
      dateTypeParam as 'Creation' | 'Payment' | undefined
    );
    const projects$ = this.projectService.getAll();
    
    refundVouchers$.subscribe({
      next: (response) => {
        if (response.isSuccess) {
          this.refundVouchers = response.data;
          this.filteredVouchers = [...this.refundVouchers];
          this.processData();
        }
        this.loadingService.hide();
      },
      error: (error) => {
        console.error('Error loading refund vouchers:', error);
        this.loadingService.hide();
      }
    });

    projects$.subscribe({
      next: (response) => {
        if (response.isSuccess) {
          // Manejar estructura paginada: data puede ser array directo o {data: [...], totalCount: ...}
          const projectsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
          this.projects = projectsData.map((project: any) => ({
            value: project.id,
            label: project.projectName
          }));
        }
      },
      error: (error) => {
        console.error('Error loading projects:', error);
      }
    });
  }

  private processData(): void {
    // Calculate summary metrics
    this.totalRefundAmount = this.refundVouchers.reduce((sum, voucher) => sum + voucher.refundAmount, 0);
    this.totalProcessedAmount = this.refundVouchers.reduce((sum, voucher) => sum + voucher.processedAmount, 0);
    this.totalPendingAmount = this.refundVouchers.reduce((sum, voucher) => sum + voucher.pendingAmount, 0);
    
    // Calculate average processing time (for completed vouchers)
    const completedVouchers = this.refundVouchers.filter(v => v.isCompleted);
    if (completedVouchers.length > 0) {
      const totalDays = completedVouchers.reduce((sum, v) => sum + v.daysToProcess, 0);
      this.avgProcessingTime = Math.round(totalDays / completedVouchers.length);
    }
    
    // Extract unique banks for filter
    const uniqueBanks = new Set(this.refundVouchers.map(v => v.bank?.id));
    this.banks = Array.from(uniqueBanks).map(bankId => {
      const bank = this.refundVouchers.find(v => v.bank?.id === bankId)?.bank;
      return { value: bankId, label: bank?.bankName || 'Desconocido' };
    });
  }

  applyFilters(): void {
    this.filteredVouchers = this.refundVouchers.filter(voucher => {
      // Search term filter
      const searchMatch = !this.searchTerm || 
        voucher.documentNumber.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        voucher.participant?.fullName?.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        voucher.concept?.toLowerCase().includes(this.searchTerm.toLowerCase());
      
      // Status filter
      const statusMatch = !this.selectedStatus || voucher.status === this.selectedStatus;
      
      // Refund type filter
      const typeMatch = !this.selectedRefundType || voucher.refundType === this.selectedRefundType;
      
      // Bank filter
      const bankMatch = !this.selectedBank || voucher.bank?.id.toString() === this.selectedBank;
      
      // Project filter
      const projectMatch = !this.selectedProject || voucher.project?.id.toString() === this.selectedProject;
      
      // 🆕 Date filtering ahora se hace server-side vía dateType param
      return searchMatch && statusMatch && typeMatch && bankMatch && projectMatch;
    });
  }

  getProcessingProgress(voucher: any): number {
    if (voucher.refundAmount <= 0) return 0;
    return (voucher.processedAmount / voucher.refundAmount) * 100;
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Pending': return 'text-amber-600';
      case 'Approved': return 'text-blue-600';
      case 'Processing': return 'text-purple-600';
      case 'Completed': return 'text-green-600';
      case 'Rejected': return 'text-red-600';
      case 'Cancelled': return 'text-gray-600';
      default: return 'text-gray-600';
    }
  }

  // 🆕 Obtener código de moneda del voucher (de la inscripción, no del proyecto)
  getCurrencyCode(voucher: any): string {
    // Prioridad: CurrencyCode del DTO > Enrollment.Currency.Code > CorporateEnrollment.Currency.Code > COP
    if (voucher?.currencyCode) {
      return voucher.currencyCode;
    }
    if (voucher?.enrollment?.currency?.code) {
      return voucher.enrollment.currency.code;
    }
    if (voucher?.corporateEnrollment?.currency?.code) {
      return voucher.corporateEnrollment.currency.code;
    }
    if (voucher?.currency?.code) {
      return voucher.currency.code;
    }
    return 'COP'; // Fallback
  }

  // 🆕 Obtener símbolo de moneda
  getCurrencySymbol(voucher: any): string {
    const code = this.getCurrencyCode(voucher);
    switch (code) {
      case 'USD': return 'US$';
      case 'EUR': return '€';
      case 'COP':
      default: return '$';
    }
  }

  // 🆕 Formatear moneda con código
  formatCurrencyWithCode(amount: number, voucher: any): string {
    const symbol = this.getCurrencySymbol(voucher);
    const code = this.getCurrencyCode(voucher);
    return `${symbol}${amount.toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (${code})`;
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.selectedStatus = '';
    this.selectedRefundType = '';
    this.selectedBank = '';
    this.selectedProject = '';
    this.fromDate = null;
    this.toDate = null;
    this.dateType = '';
    this.loadData(); // 🆕 recargar server-side sin filtros de fecha
  }
}